import * as React from 'react';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { CheckCircle, XCircle, X } from 'lucide-react';

/* ── Types ──────────────────────────────────────────────── */

type ToastVariant = 'success' | 'error';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastContextType {
  toast: (t: Omit<ToastItem, 'id'>) => void;
}

/* ── Context ────────────────────────────────────────────── */

const ToastContext = React.createContext<ToastContextType>({ toast: () => {} });

export function useToast() {
  return React.useContext(ToastContext);
}

/* ── Provider ───────────────────────────────────────────── */

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const toast = React.useCallback((t: Omit<ToastItem, 'id'>) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { ...t, id }]);
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      <ToastPrimitive.Provider duration={3500} swipeDirection="right">
        {children}

        {toasts.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            defaultOpen
            onOpenChange={(open) => { if (!open) dismiss(t.id); }}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              background: '#FAF7F3',
              border: '1px solid #D4CCBF',
              borderRadius: '6px',
              padding: '12px 14px',
              boxShadow: '0 4px 12px rgba(28,25,21,0.08), 0 2px 4px rgba(28,25,21,0.04)',
              minWidth: '240px',
              maxWidth: '360px',
              animation: 'slideUp 0.2s ease-out forwards',
            }}
          >
            {/* Icon */}
            <span style={{ marginTop: '1px', flexShrink: 0 }}>
              {t.variant === 'error' ? (
                <XCircle size={16} color="#A83830" />
              ) : (
                <CheckCircle size={16} color="#3A7A58" />
              )}
            </span>

            {/* Content */}
            <div style={{ flex: 1 }}>
              <ToastPrimitive.Title
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1C1915',
                  lineHeight: 1.4,
                }}
              >
                {t.title}
              </ToastPrimitive.Title>
              {t.description && (
                <ToastPrimitive.Description
                  style={{
                    fontSize: '12px',
                    color: '#6A6158',
                    marginTop: '2px',
                    lineHeight: 1.4,
                  }}
                >
                  {t.description}
                </ToastPrimitive.Description>
              )}
            </div>

            {/* Close */}
            <ToastPrimitive.Close
              aria-label="Fechar"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                color: '#A09890',
                flexShrink: 0,
                lineHeight: 0,
              }}
            >
              <X size={13} />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}

        <ToastPrimitive.Viewport
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 9999,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            outline: 'none',
          }}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
